/**
 * The review step: what the client confirms when nothing is paid online. A
 * booking paid at the session or free ("Confirm Booking"), or a request the
 * studio approves first ("Submit Request"), in the app's confirm words.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'review-step',
  description: 'The last checkout step when nothing is paid online: the price line, the approval note on a request, and Confirm Booking or Submit Request.',
  drawing: 'standard-only',
  options: {},
  parts: {
    price: { description: 'The price line: Pay at the session, Nothing to pay, or Collected after approval.', required: true },
    note: { description: 'On a request: This booking requires approval, and when payment is collected.', required: true },
    problem: { description: 'Why the booking did not go through.', required: true },
    failure: { description: "A booking that did not go through, in the app's words: Booking failed (or Time slot expired), why, Try Again when it can work, and Go Back.", required: true },
    retry: { description: 'Try Again: books the same booking again.', required: true, role: 'primary-action' },
    goBack: { description: 'Go Back, from a booking that did not go through: one step back.', required: true, role: 'secondary-action' },
    confirm: { description: 'Confirm Booking, or Submit Request on a request; waits while the checkout asks for the number to call.', required: true, states: ['disabled'], role: 'primary-action' },
  },
  actions: {
    confirm: { description: 'Books (or sends the request) with the held time.', args: [] },
    retry: { description: 'Books the same booking again after it did not go through (the same attempt, never twice).', args: [] },
    goBack: { description: 'Leaves a booking that did not go through: one step back.', args: [] },
  },
  events: {
    booked: 'The booking was made, or the request sent.',
  },
});

/** What the review step knows. */
export interface ReviewStepState {
  /** True when the studio approves this booking first. */
  readonly request: boolean;
  /** The price line: its label (Price or Payment), its words, and the amount (none for Collected after approval). */
  readonly price: { readonly label: string; readonly value: string; readonly amount: string | null };
  /** On a request, the approval note; null otherwise. */
  readonly note: string | null;
  /** Confirm Booking, or Submit Request. */
  readonly confirmLabel: string;
  /** True while the booking is being made. */
  readonly confirming: boolean;
  /** True while the checkout asks for the number to call (service-by-phone D5): Confirm waits for it. */
  readonly waitingForNumber: boolean;
  /** Why it did not go through; null when nothing went wrong. */
  readonly problem: string | null;
  /** A booking that did not go through, as the app's failure screen tells it; null when nothing went wrong. */
  readonly failure: { readonly title: string; readonly message: string; readonly canRetry: boolean } | null;
}

/** What a drawing can ask the review step to do. */
export interface ReviewStepActions {
  /** Book (or send the request) with the held time. */
  readonly confirm: () => void;
  /** Book the same booking again after it did not go through. */
  readonly retry: () => void;
  /** Leave a booking that did not go through: one step back. */
  readonly goBack: () => void;
}

/** What the review step tells its host. */
export interface ReviewStepEvents {
  /** The booking was made (confirmed) or the request sent (pending approval). */
  readonly booked: { readonly bookingId: string; readonly status: 'CONFIRMED' | 'PENDING_APPROVAL' };
}
